/**
 * Crear una cita pinchando el hueco (spec 015 · FR-012…FR-019).
 *
 * CÓMO FUNCIONA
 * ─────────────
 * Se elige un servicio arriba, y a partir de ahí la rejilla resalta el hueco que
 * ocuparía **con su tamaño real**: una limpieza de 40 minutos ilumina 40 minutos.
 * Al pinchar se piden los datos de la persona, y solo entonces se escribe.
 *
 * PRIMERO SE BUSCA, DESPUÉS SE CREA (FR-017)
 * ──────────────────────────────────────────
 * Y no es comodidad: duplicar a un cliente parte su historial en dos y ninguna
 * de las dos mitades está completa. En esta misma agenda hay tres «José Martín
 * Rodríguez» con tres teléfonos, que es exactamente lo que pasa cuando cada uno
 * se crea sin mirar.
 *
 * EL TELÉFONO MANDA SOBRE EL NOMBRE (FR-018)
 * ──────────────────────────────────────────
 * Dos personas se escriben igual; el teléfono es la identidad que comparten el
 * canal y la agenda. Buscar por nombre es una ayuda para ENCONTRAR, nunca el
 * criterio para decidir que dos fichas son la misma persona — por eso, al elegir
 * a alguien de la lista, lo que se copia es su teléfono.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, ErrorApi } from '../lib/api'

export interface HuecoElegido {
    dia: string
    hhmm: string
    /** `null` = la columna era un día (semana / todos juntos). El diálogo lo pide. */
    profesionalId: number | null
    servicioId: number
    minutos: number
}

export interface ProfesionalDeAgenda {
    id: number
    nombre: string
    servicioIds: number[]
}

/**
 * Quién puede hacer ese servicio. Si solo hay uno, ese — no se pregunta
 * por preguntar (spec 023 · FR-003).
 */
export function quienHaceElServicio(
    profesionales: ProfesionalDeAgenda[],
    servicioId: number,
): ProfesionalDeAgenda[] {
    return profesionales.filter((p) => p.servicioIds.includes(servicioId))
}

export function profesionalPorDefecto(
    profesionales: ProfesionalDeAgenda[],
    servicioId: number,
    yaElegido: number | null,
): number | null {
    if (yaElegido !== null) return yaElegido
    const pueden = quienHaceElServicio(profesionales, servicioId)
    return pueden.length === 1 ? (pueden[0]?.id ?? null) : null
}

interface ClienteEncontrado {
    id: number
    nombre: string
    telefono: string | null
    email: string | null
    /** Su ficha en la agenda. Es lo que evita crear un duplicado. */
    eaCustomerId: number | null
}

/** `"09:30"` + 45 → `"10:15"`. Reloj de pared, sin `Date`. */
function sumar(hhmm: string, minutos: number): string {
    const [h, m] = hhmm.split(':').map(Number)
    const t = (h ?? 0) * 60 + (m ?? 0) + minutos
    return `${String(Math.floor(t / 60) % 24).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
}

export function DialogoNuevaCita({
    accountId,
    hueco,
    profesionales,
    nombreProf,
    nombreServ,
    alCerrar,
}: {
    accountId: string
    hueco: HuecoElegido
    profesionales: ProfesionalDeAgenda[]
    nombreProf: (id: number | null) => string
    nombreServ: (id: number | null) => string
    alCerrar: () => void
}) {
    const qc = useQueryClient()
    const candidatos = quienHaceElServicio(profesionales, hueco.servicioId)
    const [profesionalId, setProfesionalId] = useState<number | null>(() =>
        profesionalPorDefecto(profesionales, hueco.servicioId, hueco.profesionalId),
    )
    const [busca, setBusca] = useState('')
    const [buscando, setBuscando] = useState(false)
    const [resultados, setResultados] = useState<ClienteEncontrado[] | null>(null)
    /**
     * A quién se eligió de la lista, si es que se eligió a alguien.
     *
     * No viaja en la petición —el servidor vuelve a preguntar por teléfono, que
     * es el único criterio que comparten canal y agenda— pero **sí se enseña**:
     * quien está en el mostrador tiene que ver que va a la ficha de siempre y no
     * a una nueva. Y si luego edita el teléfono, esto deja de valer: se olvida.
     */
    const [elegido, setElegido] = useState<ClienteEncontrado | null>(null)

    const [nombre, setNombre] = useState('')
    const [apellidos, setApellidos] = useState('')
    const [telefono, setTelefono] = useState('')
    const [email, setEmail] = useState('')

    const [error, setError] = useState<string | null>(null)
    const [aviso, setAviso] = useState<string | null>(null)

    /** FR-019 · buscar NO escribe. Solo lee. */
    async function buscar() {
        const q = busca.trim()
        if (!q) return
        setBuscando(true)
        setError(null)
        try {
            const r = await api.get<{
                clientes: ClienteEncontrado[]
                canal: { alcanzable: boolean; motivo: string | null }
            }>(`/business/${accountId}/clientes?busca=${encodeURIComponent(q)}`)

            /*
             * ⚠️ «NO SE PUDO PREGUNTAR» NO ES «NADIE COINCIDE».
             *
             * Esta ruta contesta 200 con `canal.alcanzable: false` cuando Chatwoot
             * no responde — está así a propósito— y la primera versión de este
             * diálogo lo ignoraba: pintaba «nadie coincide, escribe sus datos y se
             * creará». Con el canal caído, quien está en el mostrador crearía a
             * mano a alguien que YA existe, y le parte el historial en dos. Que es
             * exactamente lo que FR-017 viene a impedir.
             */
            if (!r.canal.alcanzable) {
                setError(
                    `No se ha podido preguntar al canal${r.canal.motivo ? ` (${r.canal.motivo})` : ''}. NO quiere decir que no exista: quiere decir que no se sabe. Si crees que ya es cliente, mejor espera.`,
                )
                setResultados(null)
                return
            }
            setResultados(r.clientes)
        } catch {
            setError('No se pudo buscar en el canal. Puedes escribir los datos a mano.')
            setResultados([])
        } finally {
            setBuscando(false)
        }
    }

    function elegir(c: ClienteEncontrado) {
        setElegido(c)
        // Se parte el nombre en dos por el primer espacio. Es una suposición y
        // por eso los dos campos quedan EDITABLES: quien lo vea mal lo arregla.
        const [n, ...resto] = c.nombre.split(' ')
        setNombre(n ?? '')
        setApellidos(resto.join(' '))
        setTelefono(c.telefono ?? '')
        setEmail(c.email ?? '')
        setResultados(null)
    }

    const mut = useMutation({
        mutationFn: async () => {
            if (profesionalId === null) throw new Error('falta el profesional')
            const inicio = `${hueco.dia} ${hueco.hhmm}:00`
            return api.post<{ id: number; atado?: boolean; motivoAtado?: string }>(
                `/business/${accountId}/agenda/citas`,
                {
                    servicioId: hueco.servicioId,
                    profesionalId,
                    inicio,
                    fin: `${hueco.dia} ${sumar(hueco.hhmm, hueco.minutos)}:00`,
                    cliente: {
                        nombre: nombre.trim(),
                        ...(apellidos.trim() ? { apellidos: apellidos.trim() } : {}),
                        ...(telefono.trim() ? { telefono: telefono.trim() } : {}),
                        ...(email.trim() ? { email: email.trim() } : {}),
                    },
                },
            )
        },
        onSuccess: async (r) => {
            await qc.invalidateQueries({ queryKey: ['calendario', accountId] })
            // ⚠️ Si no quedó atada, SE DICE Y NO SE CIERRA. Una cita sin atar es
            // alguien a quien el asistente le dirá «no tienes ninguna cita»
            // teniéndola, y enterarse ahora es la diferencia entre arreglarlo y
            // descubrirlo en el mostrador.
            if (r.atado === false) {
                setAviso(
                    `La cita se ha creado, pero no se pudo atar a su ficha del canal${r.motivoAtado ? ` (${r.motivoAtado})` : ''}. El asistente no la encontrará. Se puede arreglar desde Clientes.`,
                )
                return
            }
            alCerrar()
        },
        onError: (e) => {
            setError(mensajeDeFallo(e))
        },
    })

    const puede = nombre.trim().length > 0 && profesionalId !== null && !mut.isPending

    return (
        <div className="fixed inset-0 z-40 flex items-end justify-center overflow-y-auto bg-black/60 p-3 pt-[max(4.5rem,env(safe-area-inset-top))] sm:items-start sm:p-4 sm:pt-16">
            <div className="mb-[max(0.5rem,env(safe-area-inset-bottom))] max-h-[min(90dvh,40rem)] w-full max-w-lg overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-900 p-5 shadow-xl sm:mb-0">
                <div className="mb-4">
                    <h3 className="text-base font-medium text-neutral-100">Nueva cita</h3>
                    <p className="mt-1 text-sm text-neutral-400">
                        {nombreServ(hueco.servicioId)} · {hueco.hhmm}–{sumar(hueco.hhmm, hueco.minutos)}
                        {profesionalId !== null ? ` · ${nombreProf(profesionalId)}` : ''}
                    </p>
                </div>

                {hueco.profesionalId === null && (
                    <label className="mb-4 block">
                        <span className="mb-1 block text-xs uppercase tracking-wide text-neutral-500">
                            Profesional
                            <span className="ml-1 text-neutral-600">· obligatorio</span>
                        </span>
                        {candidatos.length === 0 ? (
                            <p className="text-sm text-amber-300">
                                Nadie de los visibles hace {nombreServ(hueco.servicioId)}.
                            </p>
                        ) : (
                            <select
                                value={profesionalId ?? ''}
                                onChange={(e) => setProfesionalId(Number(e.target.value))}
                                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm text-neutral-200"
                            >
                                <option value="">Elige quién la atiende</option>
                                {candidatos.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.nombre || `Profesional ${p.id}`}
                                    </option>
                                ))}
                            </select>
                        )}
                    </label>
                )}

                {/* FR-017 · buscar antes de crear */}
                <div className="mb-4 rounded-md border border-neutral-800 bg-neutral-950/60 p-3">
                    <label className="mb-1.5 block text-xs uppercase tracking-wide text-neutral-500">
                        ¿Ya es cliente?
                    </label>
                    <div className="flex gap-2">
                        <input
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    void buscar()
                                }
                            }}
                            placeholder="Teléfono o nombre"
                            className="min-w-0 flex-1 rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600"
                        />
                        <button
                            type="button"
                            onClick={() => void buscar()}
                            disabled={buscando}
                            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
                        >
                            {buscando ? 'Buscando…' : 'Buscar'}
                        </button>
                    </div>

                    {resultados !== null && (
                        <div className="mt-2 max-h-40 overflow-y-auto">
                            {resultados.length === 0 ? (
                                <p className="text-sm text-neutral-500">
                                    Nadie coincide. Escribe sus datos abajo y se creará.
                                </p>
                            ) : (
                                resultados.slice(0, 6).map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => elegir(c)}
                                        className="flex w-full items-baseline gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-neutral-800"
                                    >
                                        <span className="text-neutral-200">{c.nombre}</span>
                                        <span className="tabular-nums text-neutral-500">{c.telefono ?? 'sin teléfono'}</span>
                                        {c.eaCustomerId === null && (
                                            <span
                                                className="ml-auto text-xs text-amber-400"
                                                title="No está atado a la agenda. Al crear la cita se atará."
                                            >
                                                sin atar
                                            </span>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <Campo etiqueta="Nombre" valor={nombre} alCambiar={setNombre} requerido />
                    <Campo etiqueta="Apellidos" valor={apellidos} alCambiar={setApellidos} />
                    <Campo
                        etiqueta="Teléfono"
                        valor={telefono}
                        alCambiar={(v) => {
                            setTelefono(v)
                            // Si se cambia el teléfono, lo que se eligió arriba ya
                            // no se corresponde: el emparejamiento es por teléfono.
                            setElegido(null)
                        }}
                    />
                    <Campo etiqueta="Correo" valor={email} alCambiar={setEmail} />
                </div>

                {/* Sin teléfono la cita se crea, pero nadie podrá atarla después:
                    el canal identifica por teléfono. Se avisa antes, no después. */}
                {elegido && (
                    <p className="mt-3 rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
                        {elegido.eaCustomerId !== null
                            ? `Ya es cliente: la cita irá a su ficha de siempre (nº ${elegido.eaCustomerId}), con todo su historial.`
                            : 'Está en el canal pero aún no tiene ficha en la agenda. Se le creará una y quedará atada a su contacto.'}
                    </p>
                )}
                {telefono.trim() === '' && (
                    <p className="mt-3 text-xs text-amber-400">
                        Sin teléfono, el asistente no podrá relacionar esta cita con su conversación.
                    </p>
                )}

                {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
                {aviso && (
                    <p className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                        {aviso}
                    </p>
                )}

                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={alCerrar}
                        className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:bg-neutral-800"
                    >
                        {aviso ? 'Cerrar' : 'Cancelar'}
                    </button>
                    {!aviso && (
                        <button
                            type="button"
                            onClick={() => mut.mutate()}
                            disabled={!puede}
                            className="rounded-md bg-emerald-500/90 px-4 py-1.5 text-sm font-medium text-neutral-950 hover:bg-emerald-400 disabled:opacity-50"
                        >
                            {mut.isPending ? 'Creando…' : 'Crear cita'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

function Campo({
    etiqueta,
    valor,
    alCambiar,
    requerido,
}: {
    etiqueta: string
    valor: string
    alCambiar: (v: string) => void
    requerido?: boolean
}) {
    return (
        <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wide text-neutral-500">
                {etiqueta}
                {requerido && <span className="ml-1 text-neutral-600">·  obligatorio</span>}
            </span>
            <input
                value={valor}
                onChange={(e) => alCambiar(e.target.value)}
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm text-neutral-200"
            />
        </label>
    )
}

/** El motivo de la agenda, en cristiano. Los mismos nombres que usa el registro. */
function mensajeDeFallo(e: unknown): string {
    if (!(e instanceof ErrorApi)) return 'No se pudo crear la cita.'
    const c = e.cuerpo as { motivo?: string } | undefined
    if (c?.motivo === 'solape') return 'Ese hueco ya está ocupado. Elige otro.'
    if (c?.motivo === 'fuera_horario') return 'Ese hueco está fuera del horario de ese profesional.'
    return c?.motivo ? `No se pudo crear la cita: ${c.motivo}` : 'No se pudo crear la cita.'
}

/**
 * Elegir qué servicio se va a reservar (FR-012, FR-013).
 *
 * VA APARTE DEL FILTRO, Y ES DELIBERADO
 * ─────────────────────────────────────
 * Filtrar dice **qué se ve**; esto dice **qué se va a crear**. Con un solo juego
 * de botones, ocultar un servicio armaría el modo de crear sin querer — y el
 * siguiente clic en la rejilla abriría un diálogo que nadie pidió.
 *
 * Y un servicio SIN DURACIÓN declarada no se puede elegir: la duración es lo que
 * define el hueco, y sin ella el resaltado tendría que inventarse un tamaño.
 * Iluminar media hora para algo que dura una es mentir sobre lo que se reserva.
 */
export function ServiciosParaCrear({
    servicios,
    elegido,
    alElegir,
    hint,
}: {
    servicios: { id: number; nombre: string; duracionMin: number | null }[]
    elegido: number | null
    alElegir: (id: number | null) => void
    hint?: string
}) {
    if (servicios.length === 0) return null
    return (
        <div className="flex flex-wrap items-center gap-1.5">
                    <span className="mr-1 w-24 shrink-0 text-[11px] uppercase tracking-wide text-neutral-500">
                        Tipo
                    </span>
                    {servicios.map((sv) => {
                        const activo = elegido === sv.id
                        const sinDuracion = sv.duracionMin === null
                        return (
                            <button
                                key={sv.id}
                                type="button"
                                disabled={sinDuracion}
                                onClick={() => alElegir(activo ? null : sv.id)}
                                aria-pressed={activo}
                                title={
                                    sinDuracion
                                        ? 'Este servicio no tiene duración declarada, así que no se puede pintar el hueco'
                                        : 'Elígelo y pincha en el calendario'
                                }
                                className={
                                    'rounded-full border px-2.5 py-1 text-xs transition ' +
                                    (sinDuracion
                                        ? 'cursor-not-allowed border-neutral-900 text-neutral-700'
                                        : activo
                                          ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200'
                                          : 'border-neutral-800 text-neutral-400 hover:text-neutral-200')
                                }
                            >
                                {sv.nombre || `Servicio ${sv.id}`}
                                {sv.duracionMin !== null && (
                                    <span className="ml-1 text-neutral-500">{sv.duracionMin}′</span>
                                )}
                            </button>
                        )
                    })}
                    {elegido !== null && (
                        <span className="ml-1 text-xs text-emerald-300">
                            {hint ?? 'pincha el hueco en el calendario'}
                        </span>
                    )}
                </div>
    )
}
